import React, { memo, useMemo, useState } from "react";

import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
} from "react-table";
import { icon } from "../../utils/icons";
// import components
import Pagination from "./Pagination";
import SearchFilter from "../SearchFilter";

const Index = ({
  totalData = 0,
  dataPerPage = 20,
  enableSorting = true,
  enableSearch = true,
  showRecordsCount = true,
  headerStyle,
  head = [],
  body = [],
  columnsSearch= [],
  isLoading = false,
  elements = null,
  currentPage,
  setCurrentPage,
}) => {
  const columns = useMemo(() => head, [head]);
  const data = useMemo(() => body, [body]);
  const pagesNumbers = useMemo(
    () => [...Array(Math.ceil(totalData / dataPerPage)).keys()],
    [totalData, dataPerPage]
  );

  const [searchedColumn, setSearchedColumn] = useState(null);

  // distructurs props from table hook
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,

    pageCount,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      autoResetPage: false,
      initialState: {
        pageSize: dataPerPage,
        hiddenColumns: columns
          .filter((col) => col?.show === false)
          .map((col) => col.accessor),
        sortBy: columns
          .filter((c) => c.defaultSort)
          .map((c) => {
            return {
              id: c.accessor,
              desc: true,
            };
          }),
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const { globalFilter, pageIndex } = state;

  const handleGlobalFilterChange = (filterValue) => {
    setGlobalFilter(filterValue);
    // Assuming you have a way to identify the column that's being searched
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-start gap-4 flex-wrap mb-4">
        {enableSearch && (
          <SearchFilter
            disabled={data.length === 0}
            value={globalFilter}
            placeholder="البحث عن..."
            changeHandler={handleGlobalFilterChange}
          />
        )}
      </div>
      <div className="overflow-x-auto mb-4 ">
        <table
          {...getTableProps}
          className="w-full text-center table-auto overflow-x-scroll  relative"
        >
          <thead
            className={`border-b border-primary-border dark:border-primary-border-dark ${headerStyle}`}
          >
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    className={`p-4 items-center relative group whitespace-nowrap ${
                      column.id === searchedColumn
                        ? "bg-yellow-200 rounded-lg "
                        : ""
                    }`}
                    {...column.getHeaderProps(
                      (enableSorting || pageCount !== 0) &&
                        data.length > 0 &&
                        column.getSortByToggleProps()
                    )}
                  >
                    {column.render("Header")}
                    {/* عنصر اختيار الحقل */}
                
                    {enableSorting && pageCount !== 0 && (
                      <>
                        {column.isSorted ? (
                          <span
                            as="span"
                            className={`absolute top-2/4 -translate-y-2/4`}
                          >
                            {column.isSortedDesc ? icon.sortDown : icon.sortUp}
                          </span>
                        ) : (
                          <span
                            as="span"
                            className={`absolute top-2/4 -translate-y-2/4 invisible group-hover:visible`}
                          >
                            {icon.unsorted}
                          </span>
                        )}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody
            {...getTableBodyProps()}
            className={`relative ${
              pageCount === 0 && isLoading && "h-[300px]"
            }`}
          >
            {pageCount > 0 &&
              page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    className="border-b border-primary-border dark:border-primary-border-dark  last:border-0 hover:bg-third cursor-pointer dark:hover:bg-third-dark"
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell) => {
                      const cellStyle = cell.row?.original?.cellStyle;

                      return (
                        <td
                          className={`py-2 text-sm lg:text-[1rem] ${
                            cellStyle ? "bg-third dark:bg-[#14192a]" : ""
                          }`}
                          {...cell.getCellProps()}
                        >
                          {elements !== null && elements[cell.column.id]
                            ? elements[cell.column.id](cell)
                            : cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            {!isLoading && pageCount === 0 && (
              <tr className="py-2">
                <td colSpan={head.length}>
                  <h2>لا يوجد بيانات للعرض</h2>
                </td>
              </tr>
            )}
            {isLoading && (
              <tr className="absolute flex items-center top-2/4 -translate-x-2/4 -translate-y-2/4 left-2/4">
                <td>
                  <div className="circle w-[100px] h-[100px] animate-spin  rounded-full border-[6px] border-t-primary border-r-primary border-b-primary dark:border-t-primary-dark dark:border-r-primary-dark dark:border-b-primary-dark"></div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between">
        {pagesNumbers.length > 1 && (
          <Pagination
            gotoPage={setCurrentPage}
            pageIndex={currentPage}
            icon={icon}
            pagesNumber={pagesNumbers}
            nextPage={() => setCurrentPage(currentPage + 1)}
            canNextPage={currentPage + 1 < pagesNumbers.length}
            canPreviousPage={currentPage > 0}
            previousPage={() => setCurrentPage(currentPage - 1)}
          />
        )}
        {showRecordsCount && pagesNumbers.length !== 0 && (
          <p>
            عرض {pageIndex + 1} من {pagesNumbers.length} | {data.length}{" "}
            {data.length > 3 && data.length < 11 ? "سجلات" : "سجل"}
          </p>
        )}
      </div>
    </div>
  );
};

export default memo(Index);
